/**
 * ONE `Intl` FORMATTER PER (LOCALE, OPTIONS), BUILT ONCE (2026-10-02 audit).
 *
 * Constructing an `Intl.DateTimeFormat` is the expensive half of formatting —
 * measured at 134–199 µs per call against 6–20 µs for a reused one — and the
 * date helpers built a fresh one for every cell of every table on every
 * render. A formatter is immutable once built, so sharing one per key is safe.
 *
 * The key is the locale plus the options serialised in a FIXED field order, so
 * `{ year, month }` and `{ month, year }` share an entry. The map grows only
 * with distinct (zone, option-set) pairs, which in this app is a few dozen.
 */
const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const numberFormats = new Map<string, Intl.NumberFormat>();

function keyOf(locale: string | undefined, options: object): string {
  const sorted = Object.keys(options)
    .sort()
    .map((k) => `${k}=${String((options as Record<string, unknown>)[k])}`)
    .join('&');
  return `${locale ?? ''}|${sorted}`;
}

export function dateTimeFormat(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const key = keyOf(locale, options);
  let f = dateTimeFormats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, options);
    dateTimeFormats.set(key, f);
  }
  return f;
}

export function numberFormat(
  locale: string | undefined,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat {
  const key = keyOf(locale, options);
  let f = numberFormats.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, f);
  }
  return f;
}
